import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import tinycolor from 'tinycolor2';
import { isSafari } from 'react-device-detect';
import { Close } from '@material-ui/icons';

import { CHANGE_SCENE_TYPES } from '../../util/constants';
import { ArrowBack } from '../QPlayerSVGElement';
import ControlButton from '../Controls/ControlButton';
import { useConfiguration } from '../../contexts/ConfigurationContext';
import { usePlayer } from '../../contexts/PlayerContext';
import { usePresenter } from '../../contexts/PresenterContext';
import { PresenterHelper } from '../Presenter/presenter-helper';

interface RestrictionContainerProps {
	isMobile?: boolean;
	playerConfigs?: any
    children?: React.ReactNode;
    backgroundImage?: "";
    classNames?: string;
}

const Container = styled.div<RestrictionContainerProps>`
	// Trick to make the video player fill the entire container without leaving a small black border around it
	width: 100.5%;
	height: 100.5%;
	margin: -0.1%;

    background-color: #000;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    background-image: url(${(props) => props.backgroundImage});
	background-color: #000000;
    background-position: center center;
    background-size: auto 100%;
    background-repeat: no-repeat;
	z-index: 10003;
	position: absolute;
	top: 0;
    > div {
        padding 24px;
    }
`;

const RestrictionContainer: React.FC<RestrictionContainerProps> = ({
	isMobile,
	children,
	playerConfigs
}) => {
	const { t } = useTranslation();

	const { scenePath, sceneConfig,configParams, mediasData, currentMediaId, setChangeSceneType, setSceneConfig, setSceneHistory } = useConfiguration();
	const { setCurrentSceneWidgets } = usePlayer();
	const { presentationPublishedSlides } = usePresenter();
	const currentMedia = useMemo(() => mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId), [mediasData, currentMediaId]);

	const onBackButtonClick = useCallback(() => {
		if (!scenePath || scenePath.length === 0) {
			return;
		}

		const lastPath = scenePath.pop();
		setSceneConfig({ ...sceneConfig, currentScene: lastPath });
		setSceneHistory(scenePath);
		setCurrentSceneWidgets(null);
		setChangeSceneType(CHANGE_SCENE_TYPES.onclick);
	}, [scenePath, setSceneConfig, sceneConfig, setSceneHistory, setCurrentSceneWidgets, setChangeSceneType]);

	const color = playerConfigs?.playerStyling?.primaryColor ?? '#126cfc';
	const opacity = Number(playerConfigs?.playerStyling?.primaryOpacity ?? 100);
	const rgbaWithOpa = { ...tinycolor(color).toRgb(), a: opacity / 100 };
	const hoverStyle = isMobile ? {} : { backgroundColor: tinycolor(rgbaWithOpa).toRgbString() };
    const bgURL = playerConfigs?.restrictedContainer?.restrictedContainerBackground?.enabled
                    ? playerConfigs?.restrictedContainer?.restrictedContainerBackground?.url : "";
	const hasPresentation = PresenterHelper.hasPresentationLayout({ publishedSlides: presentationPublishedSlides, media: currentMedia });

	return (
		<Container isMobile={isMobile} backgroundImage={bgURL} className={"qplayer-restriction-container"} style={hasPresentation ? {} : configParams?.aspectRatio ? { aspectRatio: configParams.aspectRatio, position: "relative" } : {}}>
			<div>
				{scenePath && scenePath.length > 0 && (
					<ControlButton
						style={isMobile
							? { position: 'absolute', top: '12px', left: '12px', width: "32px", height: "32px", margin: "0" }
							: { position: 'absolute', top: '24px', left: '24px', margin: "0" }}
						hoverStyle={hoverStyle}
						tabIndex={0}
						ariaLabel={t('LABEL_GO_BACK_BTN')}
						onClick={onBackButtonClick}
						icon={<ArrowBack width={isMobile ? 18 : undefined} height={isMobile ? 15 : undefined}/>}
					/>
				)}

				{children}
			</div>
			{configParams.inline === "off" && !!configParams.container && isMobile && <ControlButton
				tabIndex={0}
				ariaLabel={t('LABEL_CLOSE')}
				tooltip={t('LABEL_CLOSE')}
				hoverStyle={hoverStyle}
				onClick={() => {
					if (configParams.container) {
						configParams.container.setAttribute('data-qplayer-modal-open', 'false');
					}
				}}
				icon={<Close htmlColor="currentColor" className={isSafari ? 'safari-fix' : ''} />}
				className='qplayer-close-modal'
				style={configParams.useLoader === "true" ? { visibility: "hidden" } : { position: "absolute", top: 0, right: 0, width: "32px" }}
				topControl
			/>}
		</Container>
	);
};

export default RestrictionContainer;
